import { useState, useEffect, useRef } from 'react';
import { gsap } from "gsap";
import Layout from "../components/global/layout"
import Cards from "./Cards"
import ScrollTrigger from "gsap/ScrollTrigger";
import ApiClient from '../methods/api/apiClient';
import loader from '../methods/loader';
import methodModel from '../methods/methods';
import CategoryFilters from './CategoryFilters';

const HomePage = () => {
    const homepage: any = sessionStorage.getItem("homepage")
    const [isVideoVisible, setIsVideoVisible] = useState(homepage === "false" ? false : true); // Track video visibility
    const sectionRef = useRef(null); // Reference to section-one
    const videoRef = useRef(null);
    const [cardData, setCardData]: any = useState([])
    const [filters, setFilters]: any = useState({ page: 1, count: 9, search: "", audio: "", categoryId: [], subCategoryId: [], start_price: "", end_price: "", status: "active" })

    const handleVideoEnd = () => {
        // Hide the video and show section-one
        setIsVideoVisible(false);
    };

    gsap.registerPlugin(ScrollTrigger);

    // Trigger the animation once `section-one` is visible
    useEffect(() => {
        if (isVideoVisible) {
            sessionStorage.clear()
        }
        if (!isVideoVisible && sectionRef.current) {
            // Ensure that GSAP animation is applied only when section-one is visible
            gsap.fromTo(
                sectionRef.current,
                { opacity: 0, scale: 0.8 }, // Initial state for animation
                { opacity: 1, scale: 1, duration: 2, ease: "power2.out" } // Final state
            );
        }
    }, [isVideoVisible]); // Runs when `isVideoVisible` changes

    useEffect(() => {
        gsap.fromTo(
            ".left1 img",
            { x: -100, opacity: 0 },
            {
                x: 0,
                opacity: 1,
                duration: 2,
                scrollTrigger: {
                    trigger: ".left1 img", // Element that triggers the animation
                    start: "top 80%", // When the element reaches 80% of the viewport height
                    end: "top 30%", // The end point of the trigger
                    scrub: true, // Ensures smooth animation as you scroll
                },
            }
        );

        gsap.fromTo(
            ".right2",
            { x: 100, opacity: 0 },
            {
                x: 0,
                opacity: 1,
                duration: 2,
                scrollTrigger: {
                    trigger: ".right2",
                    start: "top 80%",
                    end: "top 30%",
                    scrub: true,
                },
            }
        );

        gsap.fromTo(
            ".top_up",
            { y: 80, opacity: 0 },
            {
                y: 0,
                opacity: 1,
                duration: 1,
                scrollTrigger: {
                    trigger: ".top_up",
                    start: "top 80%",
                    end: "top 30%",
                    scrub: true,
                },
            }
        );

        gsap.fromTo(
            ".top_up1",
            { y: 100, opacity: 0 },
            {
                y: 0,
                opacity: 1,
                duration: 2,
                scrollTrigger: {
                    trigger: ".top_up1",
                    start: "top 80%",
                    end: "top 30%",
                    scrub: true,
                },
            }
        );


        gsap.fromTo(
            ".ones_left",
            { x: -50, opacity: 0 }, // Start from the left
            {
                x: 0,
                opacity: 1,
                duration: 1,
                scrollTrigger: {
                    trigger: ".ones_left",
                    scrub: true,
                },
            }
        );

        gsap.fromTo(
            ".ones_right",
            { x: 50, opacity: 0 }, // Start from the right
            {
                x: 0,
                opacity: 1,
                duration: 1,
                scrollTrigger: {
                    trigger: ".ones_right",
                    scrub: true,
                },
            }
        );

        gsap.fromTo(
            ".ones_left1",
            { x: -50, opacity: 0 }, // Start from the left
            {
                x: 0,
                opacity: 1,
                duration: 1,
                scrollTrigger: {
                    trigger: ".ones_left1",
                    scrub: true,
                },
            }
        );

        gsap.fromTo(
            ".ones_right2",
            { x: 50, opacity: 0 }, // Start from the right
            {
                x: 0,
                opacity: 1,
                duration: 1,
                scrollTrigger: {
                    trigger: ".ones_right2",
                    scrub: true,
                },
            }
        );
    }, []);

    const keyEmblemRef = useRef<HTMLDivElement>(null);
    const enterTextRef = useRef<HTMLDivElement>(null);
    const doorLeftRef = useRef<HTMLDivElement>(null);
    const doorRightRef = useRef<HTMLDivElement>(null);
    const servicesContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        getCardData()

        const keyEmblem = keyEmblemRef.current;
        const enterText = enterTextRef.current;
        const doorLeft = doorLeftRef.current;
        const doorRight = doorRightRef.current;
        const servicesContainer = servicesContainerRef.current;

        if (keyEmblem && enterText && doorLeft && doorRight && servicesContainer) {
            const handleKeyEmblemClick = () => {
                keyEmblem.classList.add('fade-out');

                setTimeout(() => {
                    enterText.classList.add('fade-out');
                }, 500);

                setTimeout(() => {
                    doorLeft.classList.add('open-left');
                    doorRight.classList.add('open-right');
                }, 1000);

                setTimeout(() => {
                    servicesContainer.classList.add('visible');
                    setIsVideoVisible(false)
                    sessionStorage.setItem("homepage", "false")
                }, 1500);
            };

            keyEmblem.addEventListener('click', handleKeyEmblemClick);

            enterText.addEventListener('click', () => {
                handleKeyEmblemClick();
            });

            return () => {
                keyEmblem.removeEventListener('click', handleKeyEmblemClick);
                enterText.removeEventListener('click', handleKeyEmblemClick);
            };
        }
    }, []);

    const getCardData = () => {
        loader(true)
        ApiClient.get(`card/listing`, { page: 1, count: 2, status: "active" }).then((res: any) => {
            if (res.success) {
                setCardData(res?.data)
            }
            loader(false)
        })
    }

    return (
        <>
            {isVideoVisible ?
                <div className="parentBody">
                    <div className="entrance">
                        <div className="doors-container">
                            <div className="door door-left" ref={doorLeftRef}>
                                <div className="door-texture"></div>
                                <div className="door-handle"></div>
                                <div className="keyhole"></div>
                            </div>
                            <div className="door door-right" ref={doorRightRef}>
                                <div className="door-texture1"></div>
                                <div className="door-handle"></div>
                                <div className="keyhole"></div>
                            </div>
                        </div>
                        <div className='enter-text'>
                            <div id="keyEmblem">
                                <div className='flex items-center gap-3 cursor-default'>
                                    <img src={`/assets/img/e1.png`} className='h-10 lg:h-20' />
                                    <div className="key-emblem" id="enterText" ref={enterTextRef} style={{ cursor: 'url(/assets/img/key.png), auto' }}>
                                        <div ref={keyEmblemRef}>
                                            <img src={`/assets/img/e2.png`} className='h-10 lg:h-20' />
                                        </div>
                                    </div>
                                    <img src={`/assets/img/e3.png`} className='h-10 lg:h-20' />
                                </div>
                            </div>
                        </div>
                        <div className="services-container" id="servicesContainer" ref={servicesContainerRef}>
                        </div>
                    </div>
                </div>
                :
                <Layout>
                    <div className="videos">
                        {isVideoVisible ? (
                            <div className="hero-banner py-10 w-full h-[800px] relative">
                                <video
                                    ref={videoRef}
                                    className="absolute top-0 left-0 w-full h-full object-cover"
                                    autoPlay
                                    muted
                                    playsInline
                                    onEnded={handleVideoEnd} // Trigger animation when video ends
                                >
                                    <source src="/assets/img/open.mp4" type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>
                            </div>
                        ) : (
                            <div className="section-one" >
                                <div
                                    className="hero-banner py-2 lg:py-5 w-full"
                                    style={{
                                        backgroundImage: 'url(/assets/img/ed/home1.jpg)',
                                        backgroundRepeat: 'no-repeat',
                                        backgroundSize: 'cover',
                                    }}
                                >
                                    <CategoryFilters filters={filters} setFilters={setFilters} />
                                    <div ref={sectionRef} className="grid grid-cols-12 items-center gap-4 px-4 lg:px-16">
                                        <div className="col-span-12 md:col-span-6 lg:col-span-5">
                                            <div className="relative mt-6">
                                                {/* Background Circles */}
                                                <img
                                                    src="/assets/img/bg-circles.png"
                                                    className="w-full max-h-[400px] sm:max-h-[600px] object-contain"
                                                    alt="Background"
                                                />

                                                {/* Foreground Images */}
                                                <div className="absolute top-4 sm:top-10 flex  gap-4 2xl:gap-10 items-center sm:items-start w-full px-4">
                                                    {cardData?.[0]?.envelopeDetail?.front_image &&
                                                        <img
                                                            src={methodModel.noImg(cardData?.[0]?.envelopeDetail?.front_image)}
                                                            className="w-[140px] sm:w-[150px] xl:w-[200px] 2xl:w-[250px] object-contain"
                                                            alt="Image 4"
                                                        />
                                                    }
                                                    {cardData?.[1]?.envelopeDetail?.front_image &&
                                                        <img
                                                            src={methodModel.noImg(cardData?.[1]?.envelopeDetail?.front_image)}
                                                            className="w-[200px] hidden md:block sm:w-[150px] xl:w-[200px] 2xl:w-[250px] sm:relative sm:top-[90px] object-contain"
                                                            alt="Image 5"
                                                        />
                                                    }
                                                </div>
                                            </div>

                                        </div>
                                        <div className="col-span-12 md:col-span-6 lg:col-span-5">
                                            <div className="flex flex-col gap-4 right right2">
                                                <h1 className="text-[25px] md:text-[35px] xl:text-[40px] 2xl:text-[50px] font-light">
                                                    Greetings for the moments that matter
                                                </h1>
                                                <p className="text-[20px] font-light">
                                                    Where traditional communication and modern convenience come together. Making it easier to share your thoughts with clarity and ease.
                                                </p>
                                                <div>
                                                    <button className="bg-primary px-6 py-2 text-white rounded-lg" onClick={() => {
                                                        methodModel.scrollToDiv('premiumGreeting')
                                                    }}>
                                                        Explore More
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="h-1 w-full bg-primary"></div>
                            </div>
                        )}
                    </div>
                    <div className="section-three px-4 lg:px-16 pt-6">
                        <div className="text-center text-[32px] mb-[4rem] top_up1">Our featured store</div>
                        <Cards filters={filters} setFilters={setFilters} />
                    </div>
                    <div className="section-two px-4 lg:px-16 pt-6 lg:pt-24 overflow-hidden">
                        <div className='top_up'>
                            <div className="text-center text-[32px] mb-2">What's on your Mind?</div>
                            <p className="font-[400] text-center">
                                Regardless of the circumstances, know that everything will work out.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-[3rem]">
                            <div className="bg-[#FFF8DF] ones_left">
                                <img src="/assets/img/image1.png" className="h-[300px] w-full object-contain" />
                                {/* <div className="px-[3rem] py-6 flex flex-col flex-grow min-h-[250px]">
                                    <div className="text-[32px] lg:text-[38px] 2xl:text-[45px] text-[#5E4B33] leading-[35px] lg:leading-[40px] 2xl:leading-[45px] mb-4">
                                        Card Invitations
                                    </div>
                                    <ul className="list-disc mb-3">
                                        <li className="font-[300]">Elevate your event with curated envelopes, liners, and stamps</li>
                                        <li className="font-[300]">Exclusive partner collections with world-class designers</li>
                                        <li className="font-[300]">Send in minutes via email, text, or shareable link</li>
                                    </ul>
                                    <div className="mt-auto w-fit text-[14px] border-b-[1px] border-[#5D4B33] pb-2 text-[#5D4B33] cursor-pointer">
                                        BROWSE CARDS
                                    </div>
                                </div> */}
                            </div>
                            <div className="bg-[#FFF8DF] ones_right">
                                <img src="/assets/img/Velvet_transparent.png" className="h-[300px] w-full object-contain" />
                                {/* <div className="px-[3rem] py-6 flex flex-col flex-grow min-h-[250px]">
                                    <div className="text-[32px] lg:text-[38px] 2xl:text-[45px] text-[#5E4B33] leading-[35px] lg:leading-[40px] 2xl:leading-[45px] mb-4">
                                        Card Invitations
                                    </div>
                                    <ul className="list-disc mb-3">
                                        <li className="font-[300]">Elevate your event with curated envelopes, liners, and stamps</li>
                                        <li className="font-[300]">Exclusive partner collections with world-class designers</li>
                                        <li className="font-[300]">Send in minutes via email, text, or shareable link</li>
                                    </ul>
                                    <div className="mt-auto w-fit text-[14px] border-b-[1px] border-[#5D4B33] pb-2 text-[#5D4B33] cursor-pointer">
                                        BROWSE CARDS
                                    </div>
                                </div> */}
                            </div>
                        </div>
                    </div>
                    {/* <div className="section-four px-4 lg:px-16 pt-6 lg:py-24">
                        <div className="grid grid-cols-12 gap-4 lg:gap-8 ">
                            <div className="col-span-12 lg:col-span-6  ">
                                <div className="images_boths ones_left1 flex flex-col gap-6">
                                    <div className="bg-[#C33A3A] p-6 rounded-lg">
                                        <div className="flex flex-col items-end justify-end ">
                                            <h1 className="text-[45px] font-normal text-end text-white leading-[60px]">Happy Hours
                                                <span className="!font-bold block  gap-2">Invitations  </span>

                                            </h1>
                                            <div className="mt-auto w-fit text-white text-[16px] border-b-[1px] border-[#fff] pb-2 font-light cursor-pointer">Browse </div>

                                        </div>
                                        <div className="flex gap-2 items-center">
                                            <img src="/assets/img/ed/s4.png" />

                                        </div>
                                    </div>



                                    <div className="bg-[#CCD1D2] p-6 rounded-lg">
                                        <div className="flex flex-col items-end justify-end">
                                            <h1 className="text-[45px] font-normal text-end text-[#4C4B4B] leading-[60px]">Engagement Party
                                                <span className="!font-bold block  gap-2">Invitations  </span>

                                            </h1>
                                            <div className="mt-auto w-fit text-[#4C4B4B] text-[16px] border-b-[1px] border-[#4C4B4B] pb-2 font-light cursor-pointer">Browse </div>

                                        </div>
                                        <div className="flex gap-2 items-center">
                                            <img src="/assets/img/ed/s6.png" />

                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-span-12 lg:col-span-6 ">
                                <div className="images_boths ones_right2 flex flex-col gap-6">
                                    <div className="bg-[#E8FBFF] p-6 rounded-lg">
                                        <div className="flex flex-col items-end justify-end">
                                            <h1 className="text-[45px] font-normal text-end text-[#4C4B4B] leading-[60px]">Pool Party
                                                <span className="!font-bold block  gap-2">Invitations  </span>

                                            </h1>
                                            <div className="mt-auto w-fit text-[#4C4B4B] text-[16px] border-b-[1px] border-[#4C4B4B] pb-2 font-light cursor-pointer">Browse </div>

                                        </div>
                                        <div className="flex gap-2 items-center">
                                            <img src="/assets/img/ed/s5.png" />

                                        </div>
                                    </div>



                                    <div className="bg-[#D8FFE7] p-6 rounded-lg ">
                                        <div className="flex flex-col items-end justify-end">
                                            <h1 className="text-[45px] font-normal text-end text-[#4C4B4B] leading-[60px]">Kids Bithday
                                                <span className="!font-bold block  gap-2">Invitations  </span>

                                            </h1>
                                            <div className="mt-auto w-fit text-[#4C4B4B] text-[16px] border-b-[1px] border-[#4C4B4B] pb-2 font-light cursor-pointer">Browse </div>

                                        </div>
                                        <div className="flex gap-2 items-center pt-4">
                                            <img src="/assets/img/ed/s7.png" />

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div> */}
                </Layout>
            }
        </>
    )
}

export default HomePage